<template>
  <div v-if="!redirectToUserImpact" v-on-clickaway="closeDropdown" class="personal-counter">
    <EButton
      ref="toggle"
      v-focus="dropdownOpen"
      class="personal-counter__button"
      data-test-id="personal-counter-toggle"
      @click="toggleDropdown"
    >
      <EPersonalCounterButtonContent
        :trees="trees"
        :checkpoint-icon="checkpointIcon"
        :show-indicator="showIndicator"
      />
    </EButton>

    <EPersonalCounterDropdown
      :trees="trees"
      :visible="dropdownOpen"
      :is-experiment-enabled="isExperimentEnabled"
      class="personal-counter__dropdown"
      data-test-id="personal-counter-dropdown"
    />
    <EPersonalCounterTooltip
      v-if="showTooltip && tooltipOpen"
      class="personal-counter__tooltip"
      data-test-id="personal-counter-tooltip"
      @close="closeTooltip"
    >
      {{ checkpointText }}
    </EPersonalCounterTooltip>
  </div>
  <div v-else class="personal-counter">
    <EButton
      as="nuxt-link"
      :to="userImpactTo"
      target="_blank"
      class="personal-counter__button"
      data-test-id="personal-counter-redirect"
    >
      <EPersonalCounterButtonContent
        :trees="trees"
        :checkpoint-icon="checkpointIcon"
      />
    </EButton>
  </div>
</template>

<script>
import { nextTick } from 'vue';
import { mapGetters, mapState } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import { mixin as focus } from 'vue-focus';

import {
  PERSONAL_COUNTER_TOOLTIP_SEARCH as tooltipSearchStorageKey,
  PERSONAL_COUNTER_TOOLTIP_FIRST_TREE as tooltipFirstTreeStorageKey,
  PERSONAL_COUNTER_TOOLTIP_TREE as tooltipTreeStorageKey,
  PERSONAL_COUNTER_INDICATOR_SEARCH as indicatorSearchStorageKey,
  PERSONAL_COUNTER_INDICATOR_HALF_TREE as indicatorFirstHalfTreeStorageKey,
  PERSONAL_COUNTER_INDICATOR_FIRST_TREE as indicatorFirstTreeStorageKey,
} from '~~/common/js/constants/storage-keys';
import { SEARCHES_PER_TREE, CHECKPOINTS } from '~~/common/js/constants/search';
import { sendEvent, sendCoreAnalyticsEvent } from '~~/common/js/client/analytics';
import { personalCounterUserOnboarding } from '~~/common/js/constants/feature-flags';
import { compare } from '~~/common/js/universal/version-compare';
import screen from '~~/common/vue/mixins/screen';
import EButton from '~~/common/vue/components/button';
import EPersonalCounterButtonContent from './button-content';
import EPersonalCounterDropdown from './dropdown';
import EPersonalCounterTooltip from './tooltip';

export default {
  name: 'EPersonalCounter',
  components: {
    EButton,
    EPersonalCounterButtonContent,
    EPersonalCounterDropdown,
    EPersonalCounterTooltip,
  },
  mixins: [
    clickaway,
    focus,
    screen,
  ],
  props: {
    allowImpactRedirect: {
      type: Boolean,
      default: false,
    },
    shouldIncrement: {
      type: Boolean,
      default: false,
    },
  },
  constants: {
    allowedAppVersion: '8.2.0',
    tooltipCheckpointKeys: {
      [CHECKPOINTS.FIRST_SEARCH]: tooltipSearchStorageKey,
      [CHECKPOINTS.FIRST_TREE_PLANTED]: tooltipFirstTreeStorageKey,
      [CHECKPOINTS.TREE_PLANTED]: tooltipTreeStorageKey,
    },
    indicatorCheckpointKeys: {
      [CHECKPOINTS.FIRST_SEARCH]: indicatorSearchStorageKey,
      [CHECKPOINTS.FIRST_HALF_TREE_PLANTED]: indicatorFirstHalfTreeStorageKey,
      [CHECKPOINTS.FIRST_TREE_PLANTED]: indicatorFirstTreeStorageKey,
    },
    minimumTreeCount: 2,
    // we want the checkpoint to be true in a new search
    // but only if the counter has been recently updated
    searchThreshold: 5000,
  },
  data() {
    return {
      dropdownOpen: false,
      tooltipOpen: true,
      showIndicator: false,
    };
  },
  computed: {
    ...mapState('global', ['locale']),
    ...mapState('user', [
      'browser',
      'browserVersion',
      'isEcosiaApp',
      'lastTree',
      'loggedIn',
      'treeCount',
      'isDropdownContentVisible',
    ]),
    ...mapGetters('user', [
      'nextTree',
      'treesPlanted',
      'lastCheckpoint',
    ]),
    ...mapGetters('unleash', ['getFeatureToggle']),
    isFeatureToggleEnabled() {
      return this.getFeatureToggle(personalCounterUserOnboarding.flag)?.value ===
             personalCounterUserOnboarding.enabled;
    },
    isExperimentEnabled() {
      return this.isFeatureToggleEnabled && !this.redirectToUserImpact && this.treeCount <= 75;
    },
    trees() {
      return this.treesPlanted.toLocaleString(this.locale);
    },
    checkpoint() {
      if (!this.shouldIncrement) {
        return null;
      }

      const lastIncrement = Math.abs(Date.now() - this.lastTree);
      const incrementSafePeriod = lastIncrement <= this.searchThreshold;

      if (!incrementSafePeriod) {
        return null;
      }
      if (this.treeCount === 1) {
        return CHECKPOINTS.FIRST_SEARCH;
      }
      if (this.isExperimentEnabled && this.treeCount === Math.round(SEARCHES_PER_TREE / 2)) {
        return CHECKPOINTS.FIRST_HALF_TREE_PLANTED;
      }
      if (this.isExperimentEnabled && this.nextTree === 0 && this.treesPlanted === 1) {
        return CHECKPOINTS.FIRST_TREE_PLANTED;
      }
      if (this.nextTree === 0 && this.treesPlanted > 0) {
        return CHECKPOINTS.TREE_PLANTED;
      }
      return null;
    },
    showTooltip() {
      return Object.keys(this.tooltipCheckpointKeys).includes(this.checkpoint);
    },
    checkpointIcon() {
      if (this.checkpoint === CHECKPOINTS.FIRST_SEARCH) {
        return 'leaf';
      }
      if (
        this.checkpoint === CHECKPOINTS.TREE_PLANTED ||
        this.checkpoint === CHECKPOINTS.FIRST_TREE_PLANTED
      ) {
        return 'plus-one';
      }
      return null;
    },
    tooltipStorageKey() {
      return this.tooltipCheckpointKeys[this.checkpoint] || null;
    },
    indicatorStorageKey() {
      return this.indicatorCheckpointKeys[this.lastCheckpoint] || null;
    },
    checkpointText() {
      if (!this.showTooltip) {
        return '';
      }

      if (this.isExperimentEnabled && this.checkpoint === CHECKPOINTS.FIRST_SEARCH) {
        return "You're on your way to plant your first tree! Learn how and where we plant.";
      }

      return this.$t(`common.header.counter.action.${this.checkpoint}`);
    },
    displayTrackId() {
      const suffix = this.loggedIn ? 'loggedin' : 'loggedout';
      return `personal-counter-content--${suffix}`;
    },
    redirectToUserImpact() {
      const allowedApp = this.isEcosiaApp && this.browser.includes('ios');
      const allowedVersion = compare(this.browserVersion, this.allowedAppVersion) >= 0;
      return this.allowImpactRedirect && allowedApp && allowedVersion;
    },
    userImpactTo() {
      return { path: '/your-impact' };
    },
  },
  mounted() {
    this.trackDropdownDisplay();
    if (localStorage) {
      this.tooltipOpen = !localStorage.getItem(this.tooltipStorageKey);
      this.showIndicator = this.isExperimentEnabled &&
        this.lastCheckpoint && !localStorage.getItem(this.indicatorStorageKey);
    }
    this.trackTooltipDisplay();
  },
  methods: {
    closeDropdown({ target }) {
      const { $el } = this.$refs.toggle;
      if (target !== $el && !$el.contains(target)) {
        this.dropdownOpen = false;
      }
    },
    closeTooltip() {
      this.tooltipOpen = false;
      if (this.tooltipStorageKey) {
        localStorage.setItem(this.tooltipStorageKey, '1');
      }
    },
    toggleDropdown() {
      if (this.tooltipOpen && this.showTooltip) {
        return;
      }

      const payload = { highlighted: Number(this.showIndicator) };
      this.dropdownOpen = !this.dropdownOpen;
      this.showIndicator = false;
      if (this.indicatorStorageKey) {
        localStorage.setItem(this.indicatorStorageKey, '1');
      }

      this.trackDropdownDisplay();
      sendEvent('personal-counter-pill', payload);
      sendCoreAnalyticsEvent('personalCounterPillClick', payload);
    },
    trackDropdownDisplay() {
      nextTick(() => {
        if (this.dropdownOpen) {
          const hasContentSection = Number(
            this.lastCheckpoint && this.isDropdownContentVisible,
          );
          const payload = { loggedIn: this.loggedIn, hasContentSection };

          sendEvent(this.displayTrackId, payload);
          sendCoreAnalyticsEvent('personalCounterContentDisplay', payload);
        }
      });
    },
    trackTooltipDisplay() {
      if (this.tooltipOpen && this.showTooltip) {
        const payload = { checkpoint: this.checkpoint, treeCount: this.treeCount };

        sendEvent('personal-counter-tooltip-impression', payload);
        sendCoreAnalyticsEvent('personalCounterTooltipImpression', payload);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$button-size: 48px;

.personal-counter {
  position: relative;
}

.personal-counter__button {
  color: var(--color-brand-primary);

  @include desktop-down {
    padding: $space-2s;
    height: $button-size;
    min-width: $button-size;
  }
}

.personal-counter__dropdown {
  margin-top: $space-s;
  right: $space-s;

  @include tablet {
    @include elevation(2);
  }

  @include desktop {
    left: unset;
    right: -$space-s;
  }
}

.personal-counter__tooltip {
  position: absolute;
  top: $button-size + $space-s;
  left: 0;

  @include desktop {
    left: unset;
    right: $space-l;
  }
}
</style>
